import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';

import { SCHEDULE_TEST_RUN_MAX_WAIT, SCHEDULE_TEST_RUN_POLL_INTERVAL } from '../../constants.ts';
import { GET_CONVERSATION_SCHEDULE, RUN_SCHEDULE_NOW } from '../../queries.ts';

export type WatchedTestRun =
    | { state: 'watching'; previousLastRunAt: string | null }
    | { state: 'reported'; lastRunAt: string | null }
    | { state: 'background'; previousLastRunAt: string | null };

const watchedTestRuns = new Map<string, WatchedTestRun>();

export const getWatchedTestRun = (scheduleId: string) => watchedTestRuns.get(scheduleId);
export const forgetWatchedTestRun = (scheduleId: string) => watchedTestRuns.delete(scheduleId);
const watchTestRun = (scheduleId: string, entry: WatchedTestRun) => watchedTestRuns.set(scheduleId, entry);

export interface ScheduleTestRunResult {
    ok: boolean;
    error: string | null;
    schedule: any;
}

export const useScheduleTestRun = () => {
    const [scheduleId, setScheduleId] = useState<string | null>(null);
    const [result, setResult] = useState<ScheduleTestRunResult | null>(null);
    const previousLastRunAtRef = useRef<string | null | undefined>(undefined);

    const [runScheduleNow] = useMutation(RUN_SCHEDULE_NOW);
    const { data } = useQuery(GET_CONVERSATION_SCHEDULE, {
        variables: { id: scheduleId },
        skip: !scheduleId || Boolean(result),
        fetchPolicy: 'network-only',
        pollInterval: SCHEDULE_TEST_RUN_POLL_INTERVAL,
    });

    useEffect(() => {
        const schedule = data?.agentConversationSchedule;
        if (!schedule || result || schedule.id !== scheduleId || previousLastRunAtRef.current === undefined) return;
        if (schedule.lastRunStatus === 'running') return;
        if (schedule.lastRunStatus && schedule.lastRunAt === previousLastRunAtRef.current) return;
        watchTestRun(schedule.id, { state: 'reported', lastRunAt: schedule.lastRunAt ?? null });
        setResult({ ok: schedule.lastRunStatus === 'succeeded', error: schedule.lastRunError || null, schedule });
    }, [data, result, scheduleId]);

    const watchingIdRef = useRef<string | null>(null);
    const unmountedRef = useRef(false);
    useEffect(() => {
        watchingIdRef.current = scheduleId && !result ? scheduleId : null;
    }, [scheduleId, result]);
    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            const id = watchingIdRef.current;
            const watched = id ? getWatchedTestRun(id) : undefined;
            if (watched?.state === 'watching')
                watchTestRun(id, { state: 'background', previousLastRunAt: watched.previousLastRunAt });
        };
    }, []);

    useEffect(() => {
        if (!scheduleId || result) return;
        const timeout = setTimeout(() => {
            watchTestRun(scheduleId, { state: 'background', previousLastRunAt: previousLastRunAtRef.current ?? null });
            setResult({ ok: false, error: 'RUN_FAILED', schedule: data?.agentConversationSchedule ?? null });
        }, SCHEDULE_TEST_RUN_MAX_WAIT);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scheduleId, result]);

    const start = useCallback(
        async (id: string, { resumeOnSuccess }: { resumeOnSuccess: boolean }) => {
            setResult(null);
            setScheduleId(null);
            previousLastRunAtRef.current = undefined;
            const { data: runData } = await runScheduleNow({ variables: { id, test: true, resumeOnSuccess } });
            previousLastRunAtRef.current = runData?.runAgentConversationScheduleNow?.schedule?.lastRunAt ?? null;
            watchTestRun(id, {
                state: unmountedRef.current ? 'background' : 'watching',
                previousLastRunAt: previousLastRunAtRef.current,
            });
            setScheduleId(id);
        },
        [runScheduleNow]
    );

    const reset = useCallback(() => {
        setResult(null);
        setScheduleId(null);
        previousLastRunAtRef.current = undefined;
    }, []);

    return { start, running: Boolean(scheduleId) && !result, result, reset };
};
